import React, { useState } from 'react';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    DEFAULT_OBJECT_PERMISSIONS, 
    DEFAULT_PROPERTY_PERMISSIONS, 
    ROLE_PERMISSION_PRESETS 
} from '@/hooks/usePermissions';

const MOCK_ROLES = [
    { 
        id: 'superadmin', 
        name: 'Super Admin', 
        description: 'Full unrestricted access to all modules, financial margins, and workspace settings.', 
        users: 2,
        presetKey: 'superadmin'
    },
    { 
        id: 'sales_rep', 
        name: 'Sales Pro (AE)', 
        description: 'Standard access for Account Executives. Margins & commissions restricted.', 
        users: 14,
        presetKey: 'sales_rep'
    },
    { 
        id: 'sdr_inbound', 
        name: 'SDR / Inbound Agent', 
        description: 'Leads & qualification access. Financial metrics locked, export disabled.', 
        users: 6,
        presetKey: 'sdr_inbound'
    },
    { 
        id: 'qa_auditor', 
        name: 'QA & Compliance Auditor', 
        description: 'Call & transcript review. Full audit scoring rights, pipeline locked to read-only.', 
        users: 4,
        presetKey: 'qa_auditor'
    }
];

const MODULE_OBJECTS = {
    crm: [
        { key: 'deals', title: 'Deals', desc: 'Revenue opportunities, pipeline stages, and forecasting.', icon: 'monetization_on' },
        { key: 'leads', title: 'Leads', desc: 'Inbound prospects, qualification criteria, and routing.', icon: 'filter_list' },
        { key: 'contacts', title: 'Contacts', desc: 'Direct client profiles, communication history, and titles.', icon: 'contacts' },
        { key: 'companies', title: 'Companies & Accounts', desc: 'B2B accounts, domains, and corporate firmographics.', icon: 'corporate_fare' }
    ],
    qms: [
        { key: 'call_audits', title: 'Call Audits & Scoring', desc: 'Recordings, transcripts, and AI rubric evaluations.', icon: 'record_voice_over' },
        { key: 'deal_audits', title: 'Deal Lifecycle Audits', desc: 'Contract verification, QA compliance, and stage checks.', icon: 'verified' },
        { key: 'escalations', title: 'Escalation Cases', desc: 'Customer grievances, disputes, and executive resolutions.', icon: 'warning' }
    ],
    lms: [
        { key: 'courses', title: 'Academy Training Courses', desc: 'Internal sales playbooks and enablement certifications.', icon: 'school' }
    ],
    settings: [
        { key: 'settings', title: 'Workspace Administration', desc: 'Multi-tenant settings, billing plans, and user management.', icon: 'settings' }
    ]
};

const PROPERTY_HIGHLIGHTS = {
    deals: [
        { key: 'amount', label: 'Deal Amount / Revenue' },
        { key: 'gross_margin', label: 'Gross Profit Margin % (Sensitive)' },
        { key: 'commission_rate', label: 'Rep Commission % (Sensitive)' },
        { key: 'stage', label: 'Pipeline Stage' }
    ],
    leads: [
        { key: 'phone', label: 'Phone Number' },
        { key: 'email', label: 'Email Address' },
        { key: 'annual_revenue', label: 'Estimated Revenue' }
    ],
    call_audits: [
        { key: 'score', label: 'AI QA Overall Score' },
        { key: 'transcript', label: 'Audio Transcript' },
        { key: 'coaching_notes', label: 'Supervisor Coaching Memo' }
    ]
};

export default function RolesPermissionsSettings({ isTab = false }) {
    const [selectedRole, setSelectedRole] = useState(MOCK_ROLES[1]);
    const [activeTab, setActiveTab] = useState('crm');
    const [activePropertyModalObject, setActivePropertyModalObject] = useState(null);
    const [savedNotice, setSavedNotice] = useState(false);

    // Track active permissions per role
    const [rolePermissions, setRolePermissions] = useState(() => {
        const initial = {};
        MOCK_ROLES.forEach(r => {
            const preset = ROLE_PERMISSION_PRESETS[r.presetKey] || ROLE_PERMISSION_PRESETS.sales_rep;
            initial[r.id] = {
                objects: { ...preset.objects },
                properties: { ...preset.properties }
            };
        });
        return initial;
    });

    const currentRoleData = rolePermissions[selectedRole.id] || {
        objects: DEFAULT_OBJECT_PERMISSIONS,
        properties: DEFAULT_PROPERTY_PERMISSIONS
    };

    const handleScopeChange = (objectKey, field, value) => {
        setRolePermissions(prev => ({
            ...prev,
            [selectedRole.id]: {
                ...prev[selectedRole.id],
                objects: {
                    ...prev[selectedRole.id]?.objects,
                    [objectKey]: {
                        ...(prev[selectedRole.id]?.objects?.[objectKey] || DEFAULT_OBJECT_PERMISSIONS[objectKey] || {}),
                        [field]: value
                    }
                }
            }
        }));
    };

    const handlePropertyChange = (objectKey, propKey, tier) => {
        setRolePermissions(prev => ({
            ...prev,
            [selectedRole.id]: {
                ...prev[selectedRole.id],
                properties: {
                    ...prev[selectedRole.id]?.properties,
                    [objectKey]: {
                        ...(prev[selectedRole.id]?.properties?.[objectKey] || {}),
                        [propKey]: tier
                    }
                }
            }
        }));
    };

    const handleSaveRole = () => {
        setSavedNotice(true);
        setTimeout(() => setSavedNotice(false), 2500);
    };

    const currentModuleObjects = MODULE_OBJECTS[activeTab] || [];

    return (
        <div className="h-full flex flex-col overflow-hidden bg-[var(--bg-page)] animate-fade-in font-sans">
            {!isTab && (
                <div className="flex justify-between items-center mb-6 px-6 pt-4">
                    <div>
                        <h2 className="text-xl font-bold text-[var(--text-primary)] tracking-tight">Roles & Permission Sets</h2>
                        <p className="text-xs text-[var(--text-secondary)] mt-0.5">Manage default object access and field-level security for organizational roles.</p>
                    </div>
                </div>
            )}

            {/* Horizontal Role Selector */}
            <div className="px-6 py-4 flex gap-3 overflow-x-auto border-b border-[var(--border)] bg-[var(--bg-surface)] custom-scrollbar shrink-0">
                {MOCK_ROLES.map(role => {
                    const isSelected = selectedRole.id === role.id;
                    return (
                        <div 
                            key={role.id} 
                            onClick={() => setSelectedRole(role)}
                            className={`p-3 cursor-pointer rounded-xl border transition-all flex flex-col gap-1 min-w-[210px] ${
                                isSelected 
                                    ? 'bg-[var(--bg-muted)] border-[var(--text-primary)] shadow-xs' 
                                    : 'bg-[var(--bg-surface)] border-[var(--border)] hover:border-[var(--text-tertiary)]'
                            }`}
                        >
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-bold text-[var(--text-primary)]">{role.name}</span>
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--bg-surface)] border border-[var(--border)] font-semibold text-[var(--text-secondary)]">
                                    {role.users} Users
                                </span>
                            </div>
                            <div className="text-[11px] text-[var(--text-secondary)] line-clamp-2 leading-relaxed mt-0.5">
                                {role.description}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Role Header Action Bar */}
            <div className="flex-1 flex flex-col bg-[var(--bg-page)] overflow-hidden">
                <div className="px-8 py-4 border-b border-[var(--border)] bg-[var(--bg-surface)] flex justify-between items-center shrink-0">
                    <div>
                        <h3 className="text-sm font-bold text-[var(--text-primary)] flex items-center gap-2">
                            <span>Role Policy: {selectedRole.name}</span>
                            <span className="text-[11px] px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-semibold">
                                {selectedRole.users} Assigned Users
                            </span>
                        </h3>
                        <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                            Permissions set here apply to all users assigned this role unless overridden individually.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        {savedNotice && (
                            <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                                <ObliqueIcon name="check_circle" size={14} /> Role Policy Updated!
                            </span>
                        )}
                        <button 
                            onClick={handleSaveRole}
                            className="px-4 py-1.5 rounded bg-[var(--text-primary)] text-[var(--bg-page)] text-xs font-bold cursor-pointer hover:opacity-90 shadow-xs transition-opacity"
                        >
                            Save Role Changes
                        </button>
                    </div>
                </div>

                {/* Module Sub-Tabs */}
                <div className="flex border-b border-[var(--border)] px-8 gap-8 bg-[var(--bg-surface)] shrink-0">
                    {[
                        { id: 'crm', label: 'CRM & Pipeline' },
                        { id: 'qms', label: 'Quality & Audits' },
                        { id: 'lms', label: 'Academy (LMS)' },
                        { id: 'settings', label: 'System Administration' }
                    ].map(tab => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button 
                                key={tab.id} 
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-3.5 text-xs font-bold border-b-2 cursor-pointer transition-all bg-transparent border-0 ${
                                    isActive 
                                        ? 'border-b-2 border-slate-900 text-slate-900' 
                                        : 'text-slate-500 hover:text-slate-900'
                                }`}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Objects & Scopes Table */}
                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                    <div className="max-w-4xl mx-auto space-y-4">
                        {currentModuleObjects.map(obj => {
                            const perm = currentRoleData.objects?.[obj.key] || DEFAULT_OBJECT_PERMISSIONS[obj.key] || { enabled: true };
                            const hasFieldFLS = PROPERTY_HIGHLIGHTS[obj.key];

                            return (
                                <div key={obj.key} className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5 shadow-xs">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-start gap-3">
                                            <div className="w-9 h-9 rounded-lg bg-[var(--bg-muted)] border border-[var(--border)] flex items-center justify-center text-[var(--text-primary)]">
                                                <ObliqueIcon name={obj.icon || 'folder'} size={18} />
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-bold text-[var(--text-primary)]">{obj.title}</h4>
                                                <p className="text-xs text-[var(--text-secondary)] mt-0.5">{obj.desc}</p>
                                                {hasFieldFLS && (
                                                    <button 
                                                        onClick={() => setActivePropertyModalObject(obj.key)}
                                                        className="mt-2 text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0"
                                                    >
                                                        <ObliqueIcon name="lock" size={12} /> Configure Field-Level Security & Sensitive Properties
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* 5-Column Scopes Matrix */}
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-3 border-t border-[var(--border)]">
                                        <div>
                                            <span className="block text-[10px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-1">View</span>
                                            <select 
                                                value={perm.view || 'everything'} 
                                                onChange={(e) => handleScopeChange(obj.key, 'view', e.target.value)}
                                                className="w-full text-xs font-semibold py-1 px-2 rounded border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)]"
                                            >
                                                <option value="everything">Everything</option>
                                                <option value="team_only">Team only</option>
                                                <option value="owned_only">Owned only</option>
                                                <option value="none">None</option>
                                            </select>
                                        </div>

                                        <div>
                                            <span className="block text-[10px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-1">Edit</span>
                                            <select 
                                                value={perm.edit || 'everything'} 
                                                onChange={(e) => handleScopeChange(obj.key, 'edit', e.target.value)}
                                                className="w-full text-xs font-semibold py-1 px-2 rounded border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)]"
                                            >
                                                <option value="everything">Everything</option>
                                                <option value="team_only">Team only</option>
                                                <option value="owned_only">Owned only</option>
                                                <option value="none">None</option>
                                            </select>
                                        </div>

                                        <div>
                                            <span className="block text-[10px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-1">Delete</span>
                                            <select 
                                                value={perm.delete || 'none'} 
                                                onChange={(e) => handleScopeChange(obj.key, 'delete', e.target.value)}
                                                className="w-full text-xs font-semibold py-1 px-2 rounded border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)]"
                                            >
                                                <option value="everything">Everything</option>
                                                <option value="team_only">Team only</option>
                                                <option value="owned_only">Owned only</option>
                                                <option value="none">None</option>
                                            </select>
                                        </div>

                                        <div>
                                            <span className="block text-[10px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-1">Create</span>
                                            <select 
                                                value={perm.create ? 'allowed' : 'restricted'} 
                                                onChange={(e) => handleScopeChange(obj.key, 'create', e.target.value === 'allowed')}
                                                className="w-full text-xs font-semibold py-1 px-2 rounded border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)]"
                                            >
                                                <option value="allowed">Allowed</option>
                                                <option value="restricted">Restricted</option>
                                            </select>
                                        </div>

                                        <div>
                                            <span className="block text-[10px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-1">Export</span>
                                            <select 
                                                value={perm.export ? 'allowed' : 'restricted'} 
                                                onChange={(e) => handleScopeChange(obj.key, 'export', e.target.value === 'allowed')}
                                                className="w-full text-xs font-semibold py-1 px-2 rounded border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)]"
                                            >
                                                <option value="allowed">Allowed</option>
                                                <option value="restricted">Restricted</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Field-Level Security Mini Modal */}
            <AnimatePresence>
                {activePropertyModalObject && (
                    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4">
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-[var(--bg-surface)] w-full max-w-lg rounded-2xl shadow-2xl border border-[var(--border)] overflow-hidden"
                        >
                            <div className="p-5 border-b border-[var(--border)] flex justify-between items-center bg-[var(--bg-surface)]">
                                <div>
                                    <h4 className="text-sm font-bold text-[var(--text-primary)]">
                                        Field-Level Security for {activePropertyModalObject.toUpperCase()}
                                    </h4>
                                    <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                                        Applied to role: {selectedRole.name}
                                    </p>
                                </div>
                                <button 
                                    onClick={() => setActivePropertyModalObject(null)}
                                    className="w-7 h-7 rounded-full bg-[var(--bg-muted)] flex items-center justify-center border-0 cursor-pointer text-slate-500"
                                >
                                    <ObliqueIcon name="close" size={16} />
                                </button>
                            </div>

                            <div className="p-5 space-y-4 max-h-96 overflow-y-auto custom-scrollbar">
                                {(PROPERTY_HIGHLIGHTS[activePropertyModalObject] || []).map(p => {
                                    const currentTier = currentRoleData.properties?.[activePropertyModalObject]?.[p.key] || 'edit';
                                    return (
                                        <div key={p.key} className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-subtle)] border border-[var(--border)]">
                                            <div>
                                                <div className="text-xs font-bold text-[var(--text-primary)]">{p.label}</div>
                                                <div className="text-[10px] font-mono text-[var(--text-tertiary)]">{p.key}</div>
                                            </div>
                                            <div className="flex bg-[var(--bg-surface)] rounded-md border border-[var(--border)] p-0.5">
                                                {['edit', 'view', 'restricted'].map(tier => (
                                                    <button
                                                        key={tier}
                                                        onClick={() => handlePropertyChange(activePropertyModalObject, p.key, tier)}
                                                        className={`px-2 py-0.5 text-[10px] font-bold rounded capitalize cursor-pointer transition-all ${
                                                            currentTier === tier 
                                                                ? tier === 'restricted' ? 'bg-red-500 text-white' : 'bg-slate-900 text-white' 
                                                                : 'bg-transparent text-[var(--text-secondary)]'
                                                        }`}
                                                    >
                                                        {tier === 'restricted' ? 'Hidden' : tier}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="p-4 border-t border-[var(--border)] flex justify-end bg-[var(--bg-surface)]">
                                <button 
                                    onClick={() => setActivePropertyModalObject(null)}
                                    className="px-4 py-1.5 rounded bg-[var(--text-primary)] text-[var(--bg-page)] text-xs font-bold cursor-pointer"
                                >
                                    Apply to Role
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
